import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  X,
  DollarSign,
  Check,
  Clock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw,
  PauseCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';

import dayjs from '../../../lib/day';
import { cn } from '../../../lib/utils';
import PrivateLayout from '../../layouts/PrivateLayout';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useUser } from '../../../contexts/UserContext';
import PaperworkChip from '../partners/PaperworkChip';
import {
  useFetchReferralPayouts,
  useMarkReferralPayoutPaid,
  useMarkReferralPayoutVoid,
  useReleaseReferralPayout,
} from '../../../services/referralPayouts';

// Status filter chips (empty key = no filter). "All" first, per the Operations
// table recipe (LeadHistory).
const VIEWS = [
  { key: '', label: 'All' },
  { key: 'eligible', label: 'Owed' },
  { key: 'paid', label: 'Paid' },
  { key: 'void', label: 'Void' },
];

// Filter-aware empty-state copy, keyed by the active chip.
const EMPTY_BY_VIEW = {
  '': 'No referral payouts yet.',
  eligible: 'No payouts owed.',
  paid: 'No paid payouts yet.',
  void: 'No void payouts.',
};

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const money = (amount) => usd.format(Number(amount || 0));
const fmtDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '—');

// Small warning chip for payouts whose referrer/referred contact info matched.
const SelfReferralChip = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-status-warning-bg px-2 py-0.5 text-xs font-medium text-status-warning-fg">
    <AlertTriangle className="h-3 w-3" /> Possible self-referral
  </span>
);

// Void status chip — the (required) void reason sits behind a click, same
// premium pattern as the paperwork chip, so a long note never bloats the row.
const VoidChip = ({ note, voidedAt }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => note && setOpen((o) => !o)}
        title={note ? 'View void reason' : undefined}
        className={cn(
          'inline-flex items-center whitespace-nowrap rounded-full bg-status-error-bg px-2 py-0.5 text-xs font-medium text-status-error-fg',
          note && 'cursor-pointer transition-shadow hover:ring-1 hover:ring-status-error',
        )}
      >
        Void
        {note ? <ChevronDown className="ml-1 h-3 w-3" /> : null}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-1/2 top-7 z-50 w-64 -translate-x-1/2 rounded-xl border border-border bg-card p-1 text-left shadow-lg">
            <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Void reason{voidedAt ? ` · ${fmtDate(voidedAt)}` : ''}
            </div>
            <p className="mb-0 whitespace-pre-wrap break-words px-3 pb-2 text-sm text-foreground">
              {note}
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
};

// Minimal premium modal (no react-bootstrap). Backdrop click + Escape close.
// Mounted per-payout (keyed on payoutId) so the field state is always fresh —
// the date defaults to *today's* today, and references never leak across payouts.
const MarkPaidModal = ({ payout, onClose, onSubmit, isSaving }) => {
  const [paidAt, setPaidAt] = useState(dayjs().format('YYYY-MM-DD'));
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1060] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-base font-semibold text-foreground">Mark payout paid</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div className="rounded-lg bg-neutral-100 p-3 text-sm dark:bg-neutral-800">
            <div className="font-medium text-foreground">
              {money(payout.amount)} to {payout.payee.name}
            </div>
            <div className="text-muted-foreground">
              for referring {payout.referredClient.name}
            </div>
          </div>
          {payout.selfReferralFlag ? (
            <div className="flex items-start gap-2 rounded-lg bg-status-warning-bg p-3 text-sm text-status-warning-fg">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Possible self-referral — the referrer&apos;s contact info matches the referred
                client&apos;s. Double-check before paying.
              </span>
            </div>
          ) : null}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Date paid
            </label>
            <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Reference (check #, transfer id)
            </label>
            <Input
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Method
            </label>
            <Input
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              placeholder="e.g. Bill pay, Zelle, check"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => onSubmit({ paidAt, paymentReference, paymentMethod })}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Mark paid
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirm voiding an eligible payout (refund clawback, fraud, data mistake).
// Terminal, so a note explaining why is required. Also keyed per-payout.
const VoidModal = ({ payout, onClose, onSubmit, isSaving }) => {
  const [note, setNote] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1060] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-base font-semibold text-foreground">Void payout</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div className="rounded-lg bg-neutral-100 p-3 text-sm dark:bg-neutral-800">
            <div className="font-medium text-foreground">
              {money(payout.amount)} to {payout.payee.name}
            </div>
            <div className="text-muted-foreground">
              for referring {payout.referredClient.name}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Voiding removes this payout from the owed list permanently — this client can never
            generate another payout through the app, and it can&apos;t be undone here. Use it for
            refunds, confirmed self-referrals, or data mistakes.
          </p>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Why is this being voided? (required)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Client refunded — referral no longer qualifies"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => onSubmit({ note: note.trim() })}
              disabled={isSaving || !note.trim()}
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Void payout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Why a held payout is held, from the referrer's LIVE state (not the reason
// stamped at mint time), so the same row reads "ready" the moment they qualify.
const heldReasonLabel = (payee) => {
  if (payee.payable) return 'Ready to add';
  if (payee.status === 'active') return 'Approved, paperwork not attached';
  if (payee.status === 'pending_review' || payee.status === 'follow_up') {
    return 'Signed, awaiting review';
  }
  if (payee.status === 'rejected') return 'Rejected';
  if (payee.status === 'inactive') return 'Deactivated';
  // pending_paperwork covers "never started" AND "mid-signing", so say what is
  // true of both.
  return 'Paperwork not signed';
};

// Held payouts: money recorded for referrers who are NOT yet an approved
// partner with signed paperwork on file (Nic, 2026-09-04). They never appear
// in the page's list or tiles; this modal is their only surface. The list is
// fetched fresh on every open, so a referrer who signed and was approved since
// the page loaded shows "Add to payouts" right away. Same hand-rolled dialog
// recipe as MarkPaidModal, wider for the table.
// `escapeEnabled` is false while the Void dialog sits on top, so one Escape
// closes only the topmost dialog instead of both.
const HeldPayoutsModal = ({
  onClose,
  canManage,
  onRelease,
  onVoid,
  releasingId,
  escapeEnabled = true,
}) => {
  const [page, setPage] = useState(1);
  // Mounting the query refetches on its own (no staleTime configured), so every
  // open reads the referrers' live state without an explicit refetch call.
  const { isLoading, isError, data, refetch } = useFetchReferralPayouts('held', page);
  const rows = data?.payouts || [];
  const limit = data?.limit || 25;
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    if (!escapeEnabled) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, escapeEnabled]);

  useEffect(() => {
    if (!isLoading && page > 1 && rows.length === 0 && total > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [isLoading, page, rows.length, total, totalPages]);

  return (
    <div
      className="fixed inset-0 z-[1060] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-5xl rounded-xl border border-border bg-card shadow-lg">
        <div className="relative border-b border-border px-5 py-3 pr-12">
          <h3 className="text-base font-semibold text-foreground">Held payouts</h3>
          <p className="text-xs text-muted-foreground">
            Referrals that paid, where the referrer is not yet an approved partner with signed
            paperwork on file. Nothing here is owed until it is added to the page.
          </p>
          {/* Pinned to the corner, clear of the two-line subtitle. */}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-card">
              <tr className="border-b border-border">
                <Th>Referrer</Th>
                <Th>Referred client</Th>
                <Th>Why held</Th>
                <Th className="text-center">Paperwork</Th>
                <Th className="text-right">Amount</Th>
                <Th className="text-center">Action</Th>
              </tr>
            </thead>
            <tbody>
              {isError ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <AlertTriangle className="h-6 w-6 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Could not load held payouts.
                      </span>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="mr-2 h-4 w-4" /> Retry
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : isLoading && !data ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    Nothing held. Every owed payout belongs to an approved partner with paperwork
                    on file.
                  </td>
                </tr>
              ) : (
                rows.map((p) => (
                  <tr key={p.payoutId} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <div className="whitespace-nowrap font-medium text-foreground">
                        {p.payee.name || '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.payee.email || p.payee.phone || ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="whitespace-nowrap text-foreground">
                        {p.referredClient.name || '—'}
                      </div>
                      {p.selfReferralFlag ? (
                        <div className="mt-1">
                          <SelfReferralChip />
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {p.payee.payable ? (
                        <Badge className="whitespace-nowrap bg-status-success-bg text-status-success-fg">
                          {heldReasonLabel(p.payee)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">{heldReasonLabel(p.payee)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.payee.paperwork?.length ? (
                        <PaperworkChip docs={p.payee.paperwork} canView />
                      ) : (
                        <Badge className="bg-status-warning-bg text-status-warning-fg">
                          Pending
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">
                      {money(p.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {canManage ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <Button
                            size="sm"
                            disabled={!p.payee.payable || releasingId === p.payoutId}
                            title={
                              p.payee.payable
                                ? 'Move this payout onto the Referral Payouts page as owed'
                                : 'Available once the referrer is an approved partner with signed paperwork attached'
                            }
                            onClick={() => onRelease(p)}
                          >
                            {releasingId === p.payoutId ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Add to payouts
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => onVoid(p)}>
                            Void
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-border px-5 py-2 text-xs text-muted-foreground">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, tone, loading }) => (
  <Card>
    <CardContent className="flex items-center gap-3 p-4">
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full',
          tone === 'owed'
            ? 'bg-status-warning-bg text-status-warning-fg'
            : 'bg-status-success-bg text-status-success-fg',
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        {loading ? (
          <div className="my-1.5 h-6 w-16 animate-pulse rounded-md bg-muted" />
        ) : (
          <div className="text-2xl font-bold text-foreground">{value}</div>
        )}
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </CardContent>
  </Card>
);

// Header cell, matching the LeadHistory table recipe.
const Th = ({ children, className }) => (
  <th
    className={cn(
      'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground',
      className,
    )}
  >
    {children}
  </th>
);

const ReferralPayouts = () => {
  const { hasPermission } = useUser();
  // referral_payouts:view grants the page (read-only); :manage unlocks Mark paid / Void.
  const canManage = hasPermission('referral_payouts:manage');
  const [view, setView] = useState('eligible');
  const [page, setPage] = useState(1);
  const [marking, setMarking] = useState(null);
  const [voiding, setVoiding] = useState(null);

  const { isLoading, isError, data, refetch } = useFetchReferralPayouts(view || undefined, page);
  const markPaid = useMarkReferralPayoutPaid();
  const markVoid = useMarkReferralPayoutVoid();
  const release = useReleaseReferralPayout();
  // The Held pill is not a view: it opens the off-page modal (see HeldPayoutsModal).
  const [heldOpen, setHeldOpen] = useState(false);
  const [releasingId, setReleasingId] = useState(null);

  const payouts = data?.payouts || [];
  const limit = data?.limit || 25;
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const firstShown = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastShown = Math.min(page * limit, total);

  // Mark-paid/void can empty the current filtered page — snap back instead of
  // showing a false empty state.
  useEffect(() => {
    if (!isLoading && page > 1 && payouts.length === 0 && total > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [isLoading, page, payouts.length, total, totalPages]);

  const summary = useMemo(
    () =>
      data?.summary || {
        totalOwed: 0,
        owedCount: 0,
        totalPaid: 0,
        paidCount: 0,
        heldCount: 0,
        totalHeld: 0,
      },
    [data],
  );

  const handleMarkPaid = async (values) => {
    try {
      await markPaid.mutateAsync({ payoutId: marking.payoutId, ...values });
      toast.success('Payout marked paid');
      setMarking(null);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not mark payout paid');
    }
  };

  const handleVoid = async ({ note }) => {
    try {
      await markVoid.mutateAsync({ payoutId: voiding.payoutId, note });
      toast.success('Payout voided');
      setVoiding(null);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not void payout');
    }
  };

  // Held -> owed. The server re-checks the payable rule, so a stale modal row
  // fails loud instead of releasing someone who no longer qualifies.
  const handleRelease = async (payout) => {
    setReleasingId(payout.payoutId);
    try {
      await release.mutateAsync({ payoutId: payout.payoutId });
      toast.success(
        `${money(payout.amount)} to ${payout.payee.name || 'the referrer'} added to payouts`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not add payout');
    } finally {
      setReleasingId(null);
    }
  };

  const showSkeletonTiles = isLoading && !data;

  return (
    <PrivateLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Referral Payouts</h1>
          <p className="text-sm text-muted-foreground">
            $250 owed to referrers when a client they referred wins and pays. Pay via bank
            bill-pay, then record it here.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            icon={DollarSign}
            label="Total owed"
            value={isError ? '—' : money(summary.totalOwed)}
            tone="owed"
            loading={showSkeletonTiles}
          />
          <SummaryCard
            icon={Clock}
            label="Awaiting payment"
            value={isError ? '—' : summary.owedCount}
            tone="owed"
            loading={showSkeletonTiles}
          />
          <SummaryCard
            icon={DollarSign}
            label="Total paid"
            value={isError ? '—' : money(summary.totalPaid)}
            tone="paid"
            loading={showSkeletonTiles}
          />
          <SummaryCard
            icon={Check}
            label="Payouts paid"
            value={isError ? '—' : summary.paidCount}
            tone="paid"
            loading={showSkeletonTiles}
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {VIEWS.map((v) => (
            <button
              key={v.key || 'all'}
              type="button"
              onClick={() => {
                setView(v.key);
                setPage(1);
              }}
              className={cn(
                'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                view === v.key
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {v.label}
            </button>
          ))}
          {/* Not a filter: opens the off-page list of held payouts. Count
              badge borrows the Partners page Review Queue recipe. */}
          <button
            type="button"
            onClick={() => setHeldOpen(true)}
            className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            title="Payouts recorded for referrers who are not yet approved partners with paperwork on file"
          >
            <PauseCircle className="h-3.5 w-3.5" />
            Held
            <span
              className={cn(
                'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                summary.heldCount > 0
                  ? 'bg-status-warning-bg text-status-warning-fg'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {summary.heldCount || 0}
            </span>
          </button>
        </div>

        {/* overflow-visible (not hidden): the PaperworkChip popover must be
            able to escape the table bounds on the last rows. */}
        <div className="overflow-visible rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-card">
              <tr className="border-b border-border">
                <Th>Referrer</Th>
                <Th>Referred client</Th>
                <Th className="text-center">Paperwork</Th>
                <Th className="text-right">Amount</Th>
                <Th className="text-center">Status</Th>
                <Th className="text-center">Action</Th>
              </tr>
            </thead>
            <tbody>
              {isError ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <AlertTriangle className="h-6 w-6 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Could not load referral payouts.
                      </span>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="mr-2 h-4 w-4" /> Retry
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                  </td>
                </tr>
              ) : payouts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                    {EMPTY_BY_VIEW[view] || EMPTY_BY_VIEW['']}
                  </td>
                </tr>
              ) : (
                payouts.map((p) => (
                  <tr
                    key={p.payoutId}
                    className="border-b border-border last:border-0 hover:bg-accent/40"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{p.payee.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.payee.email || p.payee.phone || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-foreground">
                        {p.referredClient.name}
                        {p.referredClient.deleted ? (
                          <span className="ml-1 text-xs text-muted-foreground">
                            (deleted account)
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground">{p.referredClient.email}</div>
                      {p.selfReferralFlag ? (
                        <div className="mt-1">
                          <SelfReferralChip />
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {/* One chip, docs behind a click (shared PaperworkChip —
                          same premium pattern + vocabulary as the Partners
                          page): Signed ✓ / Legacy (active payee, docs signed
                          outside the system and not yet uploaded) / Pending. */}
                      {p.payee.paperwork?.length ? (
                        <PaperworkChip docs={p.payee.paperwork} canView />
                      ) : p.payee.paperworkComplete ? (
                        <Badge
                          className="bg-muted text-muted-foreground"
                          title="Paperwork was signed outside the system — upload it from the Partners page to attach it here."
                        >
                          Legacy
                        </Badge>
                      ) : (
                        <Badge className="bg-status-warning-bg text-status-warning-fg">
                          Pending
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-foreground">
                      {money(p.amount)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.status === 'paid' ? (
                        <div>
                          <Badge className="bg-status-success-bg text-status-success-fg">
                            Paid
                          </Badge>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {fmtDate(p.paidAt)}
                            {p.paymentReference ? ` · ${p.paymentReference}` : ''}
                          </div>
                        </div>
                      ) : p.status === 'void' ? (
                        <VoidChip note={p.voidNote} voidedAt={p.voidedAt} />
                      ) : p.status === 'held' ? (
                        // Defensive: the API keeps held rows off this list.
                        <Badge className="bg-muted text-muted-foreground">Held</Badge>
                      ) : (
                        <Badge className="bg-status-warning-bg text-status-warning-fg">
                          Owed
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.status === 'eligible' && canManage ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <Button size="sm" onClick={() => setMarking(p)}>
                            Mark paid
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setVoiding(p)}>
                            Void
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total === 0 ? 'No payouts' : `Showing ${firstShown}–${lastShown} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Keyed on payoutId + conditionally mounted so each open starts with fresh
          state (today's date, blank reference) instead of whatever a previous
          payout — or a previous *day* — left behind. */}
      {marking && canManage ? (
        <MarkPaidModal
          key={marking.payoutId}
          payout={marking}
          onClose={() => setMarking(null)}
          onSubmit={handleMarkPaid}
          isSaving={markPaid.isLoading}
        />
      ) : null}

      {heldOpen ? (
        <HeldPayoutsModal
          onClose={() => setHeldOpen(false)}
          canManage={canManage}
          onRelease={handleRelease}
          onVoid={(p) => setVoiding(p)}
          releasingId={releasingId}
          escapeEnabled={!voiding}
        />
      ) : null}

      {/* Rendered after the held modal so a void started from it stacks on top. */}
      {voiding && canManage ? (
        <VoidModal
          key={voiding.payoutId}
          payout={voiding}
          onClose={() => setVoiding(null)}
          onSubmit={handleVoid}
          isSaving={markVoid.isLoading}
        />
      ) : null}
    </PrivateLayout>
  );
};

export default ReferralPayouts;
